export const URDF_IMPORT_CHOICE_EVENT = "critter-urdf-import-choice";

const HELPER_URDF_TERMS = [
  "common",
  "common_properties",
  "property",
  "properties",
  "macro",
  "material",
  "materials",
  "transmission",
  "transmissions",
  "gazebo",
  "ros2_control",
  "control",
  "plugin",
  "include",
  "sensor",
  "sensors",
];

const FULL_ROBOT_URDF_TERMS = ["robot", "model", "main", "description", "base"];

export const getUploadRelativePath = (file) =>
  String(file?.webkitRelativePath || file?.name || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

export const isUrdfUploadFile = (file) => /\.(urdf|xacro)$/i.test(getUploadRelativePath(file));

const getUrdfCandidateStem = (basename) =>
  basename.replace(/(?:\.urdf)?\.xacro$/i, "").replace(/\.urdf$/i, "");

export const getUrdfDisplayPath = (file, candidates) => {
  const path = getUploadRelativePath(file);
  const candidatePaths = candidates.map(getUploadRelativePath).filter(Boolean);
  const firstSegments = candidatePaths.map((candidatePath) => candidatePath.split("/")[0] || "");
  const commonFirstSegment =
    firstSegments.length > 0 &&
    firstSegments.every((segment) => segment && segment === firstSegments[0])
      ? firstSegments[0]
      : "";
  if (commonFirstSegment && path.startsWith(`${commonFirstSegment}/`)) {
    return path.slice(commonFirstSegment.length + 1);
  }
  return path || String(file?.name || "robot.urdf");
};

const scoreUrdfCandidate = (file, allCandidates) => {
  const path = getUploadRelativePath(file).toLowerCase();
  const displayPath = getUrdfDisplayPath(file, allCandidates).toLowerCase();
  const parts = displayPath.split("/").filter(Boolean);
  const basename = parts[parts.length - 1] || displayPath;
  const stem = getUrdfCandidateStem(basename);
  let score = 0;

  if (parts.length <= 1) score += 18;
  if (basename === "model.urdf") score += 20;
  if (basename === "model.xacro" || basename === "model.urdf.xacro") score += 20;
  if (basename === "robot.urdf") score += 18;
  if (basename === "robot.xacro" || basename === "robot.urdf.xacro") score += 18;
  if (FULL_ROBOT_URDF_TERMS.some((term) => stem === term || stem.includes(`_${term}`))) score += 8;
  if (path.includes("/urdf/")) score += 5;
  if (path.includes("/robots/")) score += 4;
  if (stem.includes("description")) score += 4;
  if (HELPER_URDF_TERMS.some((term) => stem === term || stem.includes(term))) score -= 30;
  if (path.includes("/test/") || path.includes("/tests/")) score -= 10;
  if (path.includes("/example") || path.includes("/demo")) score -= 4;

  return score;
};

export const orderUrdfCandidates = (candidates) =>
  [...candidates].sort((a, b) => {
    const scoreDelta = scoreUrdfCandidate(b, candidates) - scoreUrdfCandidate(a, candidates);
    if (scoreDelta !== 0) return scoreDelta;
    return getUrdfDisplayPath(a, candidates).localeCompare(getUrdfDisplayPath(b, candidates));
  });

export const markPreferredUrdfImportRoot = (file) => {
  if (!file) return file;
  try {
    Object.defineProperty(file, "__critterPreferredImportRoot", {
      value: true,
      configurable: true,
    });
  } catch {
    file.__critterPreferredImportRoot = true;
  }
  return file;
};

export const prioritizeFileForImport = (files, selectedFile) => {
  const selected = markPreferredUrdfImportRoot(selectedFile);
  return [selected, ...files.filter((file) => file !== selected)];
};

export const prepareFilesForUrdfImport = async (files) => {
  const urdfFiles = files.filter(isUrdfUploadFile);
  if (urdfFiles.length === 0) return files;
  if (urdfFiles.length === 1) return prioritizeFileForImport(files, urdfFiles[0]);

  const selectedFile = await requestUrdfImportChoice(urdfFiles);
  if (!selectedFile) return null;
  return prioritizeFileForImport(files, selectedFile);
};

export const requestUrdfImportChoice = (candidates) =>
  new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent(URDF_IMPORT_CHOICE_EVENT, {
        detail: { candidates, resolve },
      })
    );
  });
